import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Modal,
  View,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Dimensions,
  StatusBar,
  Linking,
  Text,
} from 'react-native';
import { Caption } from './TextStyles';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';

const YoutubePlayer = Platform.OS !== 'web' ? require('react-native-youtube-iframe').default : null;
const WebView = Platform.OS !== 'web' ? require('react-native-webview').default : null;
const { Video, ResizeMode } = require('expo-av');

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('screen');

// ─── USER AGENT ──────────────────────────────────────────────────────────────
// Using a specific Mobile Safari User-Agent ensures we get the most mobile-optimized Shorts player
const MOBILE_UA = 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/16.6 Mobile/15E148 Safari/604.1';

// ─── INJECTED JAVASCRIPT ────────────────────────────────────────────────────
// Aggressive script to hide all YouTube chrome and block navigation
const CLEAN_YOUTUBE_JS = `
  (function() {
    const style = document.createElement('style');
    style.innerHTML = \`
      /* Hide Header and Navigation */
      ytm-mobile-topbar-renderer, 
      ytm-pivot-bar-renderer, 
      header, 
      #header-bar,
      .ytp-chrome-top,
      .ytp-show-cards-title,
      .ytp-share-button,
      .ytp-watch-later-button,
      .ytp-pause-overlay,
      .ytp-ce-element { 
        display: none !important; 
      }

      /* Hide Shorts Action Buttons (Like, Dislike, Comment, Share) */
      .ytm-shorts-single-video-renderer .action-container,
      .shorts-action-menu,
      .ytm-item-section-renderer,
      ytm-shorts-action-menu-renderer { 
        display: none !important; 
      }

      /* Hide Video Info (Channel, Title, Subscribe Button) */
      .shorts-video-information-container,
      .ytm-shorts-player-video-info,
      .ytm-shorts-single-video-renderer .channel-container,
      ytm-subscribe-button-renderer { 
        display: none !important; 
      }

      /* Hide YouTube Player Chrome (Captions, Watermarks, Logo) */
      .ytp-chrome-top,
      .ytp-title,
      .ytp-title-text,
      .ytp-title-channel,
      .ytp-title-link,
      .ytp-show-cards-title,
      .ytp-suggestion-set,
      .ytp-upnext,
      .ytp-pause-overlay,
      .ytp-watermark,
      .ytp-youtube-button,
      .player-controls-top,
      .ytp-gradient-top,
      .branding-img,
      .branding-context-container-inner { 
        display: none !important; 
      }

      /* Force Video to Fill Screen as much as possible */
      .shorts-video-player, 
      .ytm-shorts-single-video-renderer, 
      video {
        width: 100vw !important;
        height: 100vh !important;
        object-fit: contain !important;
      }

      /* Global Reset */
      html, body {
        overflow: hidden !important;
        background: #000 !important;
        height: 100vh !important;
        width: 100vw !important;
        margin: 0 !important;
        padding: 0 !important;
        touch-action: none !important;
      }
    \`;
    document.head.appendChild(style);

    // Continuous cleaner to remove dynamic elements
    const cleanInterval = setInterval(function() {
      const selectors = [
        '.ytp-chrome-top',
        '.ytp-show-cards-title',
        '.ytp-watermark',
        '.ytp-youtube-button',
        '.ytp-pause-overlay',
        'ytm-mobile-topbar-renderer',
        'ytm-pivot-bar-renderer',
        '.shorts-action-menu',
        '.shorts-video-information-container',
        'ytm-subscribe-button-renderer',
        '.ytp-title',
        '.ytp-title-text',
        '.ytp-gradient-top',
        '.ytp-watermark'
      ];
      
      selectors.forEach(s => {
        const el = document.querySelector(s);
        if (el) el.style.setProperty('display', 'none', 'important');
      });

      // Try to disable captions
      const video = document.querySelector('video');
      if (video && video.textTracks) {
        for (let i = 0; i < video.textTracks.length; i++) {
          video.textTracks[i].mode = 'hidden';
        }
      }
    }, 500);

    // Block vertical swipe gestures
    document.addEventListener('touchmove', function(e) {
      if (e.touches.length > 1) return; // Allow pinch zoom if any
      e.preventDefault();
    }, { passive: false });

    setTimeout(() => clearInterval(cleanInterval), 10000);
  })();
  true;
`;


const VideoModal = ({ isVisible, videoUrl, onClose }) => {
  const [playing, setPlaying] = useState(true);
  const [loading, setLoading] = useState(true);

  const getVideoId = (url) => {
    if (!url || typeof url !== 'string') return '';
    // Standard, mobile and Shorts URLs
    let match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (match) return match[1];

    return '';
  };

  const videoRef = useRef(null);
  const [status, setStatus] = useState({});
  const [showControls, setShowControls] = useState(true);
  const controlsTimeout = useRef(null);

  const isYouTube = videoUrl?.includes('youtube.com') || videoUrl?.includes('youtu.be');
  const isShort = videoUrl?.includes('/shorts/');
  const videoId = isYouTube ? getVideoId(videoUrl) : null;

  // Auto-hide controls
  useEffect(() => {
    if (showControls && !isYouTube) {
      if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
      controlsTimeout.current = setTimeout(() => {
        if (!status.isBuffering && status.isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    }
  }, [showControls, status.isPlaying, status.isBuffering, isYouTube]);

  const togglePlayback = async () => {
    if (videoRef.current) {
      if (status.isPlaying) {
        await videoRef.current.pauseAsync();
      } else {
        await videoRef.current.playAsync();
      }
    }
  };

  const handleProgressPress = async (e) => {
    if (videoRef.current && status.durationMillis) {
      const { locationX } = e.nativeEvent;
      const percent = locationX / (SCREEN_WIDTH - 80);
      const position = percent * status.durationMillis;
      await videoRef.current.setPositionAsync(position);
    }
  };

  const formatTime = (millis) => {
    if (!millis) return '00:00';
    const totalSeconds = millis / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const onStateChange = useCallback((state) => {
    if (state === 'ended') setPlaying(false);
  }, []);

  const handleClose = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setPlaying(false);
    setLoading(true);
    onClose();
  };

  const renderDirectVideo = () => {
    return (
      <TouchableOpacity
        style={styles.container}
        activeOpacity={1}
        onPress={() => setShowControls(true)}
      >
        <Video
          ref={videoRef}
          source={{ uri: videoUrl }}
          style={styles.fullScreen}
          resizeMode={ResizeMode.CONTAIN}
          shouldPlay={true}
          isLooping={true}
          onPlaybackStatusUpdate={s => setStatus(() => s)}
          onLoadStart={() => setLoading(true)}
          onLoad={() => setLoading(false)}
        />

        {loading && (
          <View style={styles.loader}>
            <ActivityIndicator size="large" color="#7c3aed" />
          </View>
        )}

        {showControls && (
          <View style={styles.controlsOverlay}>
            <TouchableOpacity
              style={styles.playPauseBtn}
              onPress={togglePlayback}
            >
              <Ionicons
                name={status.isPlaying ? 'pause' : 'play'}
                size={42}
                color="#FFF"
              />
            </TouchableOpacity>

            <View style={styles.bottomControls}>
              <View style={styles.timeRow}>
                <Caption style={styles.timeText}>{formatTime(status.positionMillis)}</Caption>
                <Caption style={styles.timeText}>{formatTime(status.durationMillis)}</Caption>
              </View>

              <TouchableOpacity
                activeOpacity={1}
                onPress={handleProgressPress}
                style={styles.progressBarWrapper}
              >
                <View style={styles.progressBarBackground}>
                  <View
                    style={[
                      styles.progressBarFill,
                      { width: `${(status.positionMillis / status.durationMillis) * 100 || 0}%` }
                    ]}
                  />
                </View>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const renderContent = () => {
    if (!isYouTube) return renderDirectVideo();
    if (!videoId) return null;

    /* ── Web Implementation ── */
    if (Platform.OS === 'web') {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&modestbranding=1&rel=0&cc_load_policy=0`}
          style={styles.webVideo}
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
        />
      );
    }

    /* ── Mobile YouTube Implementation ── */
    if (YoutubePlayer) {
      const playerHeight = isShort ? SCREEN_HEIGHT : SCREEN_WIDTH * (9 / 16);

      return (
        <View style={[styles.fullScreen, { justifyContent: 'center' }]}>
          <YoutubePlayer
            height={playerHeight}
            width={SCREEN_WIDTH}
            play={playing}
            videoId={videoId}
            onChangeState={onStateChange}
            onReady={() => setLoading(false)}
            initialPlayerParams={{
              controls: 1,
              rel: 0,
              fs: 1,
              playsinline: 1,
              modestbranding: 1,
              iv_load_policy: 3,
              autoplay: 1,
              mute: 0,
            }}
            webViewProps={{
              androidLayerType: 'hardware',
              allowsInlineMediaPlayback: true,
              mediaPlaybackRequiresUserAction: false,
              scrollEnabled: false,
              bounces: false,
              allowsFullscreenVideo: true,
              injectedJavaScript: CLEAN_YOUTUBE_JS,
              originWhitelist: ['*'],
              onError: () => {
                setLoading(false);
                Linking.openURL(videoUrl);
                handleClose();
              },
            }}
          />
          {loading && (
            <View style={styles.loader}>
              <ActivityIndicator size="large" color="#FFFFFF" />
            </View>
          )}
        </View>
      );
    }

    return null;
  };

  return (
    <Modal
      animationType="fade"
      transparent={false}
      visible={isVisible}
      onRequestClose={handleClose}
      statusBarTranslucent={true}
      presentationStyle="fullScreen"
      supportedOrientations={['portrait']}
    >
      <StatusBar hidden />
      <View style={styles.container}>
        {renderContent()}

        <TouchableOpacity
          style={styles.closeBtn}
          onPress={handleClose}
          hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
        >
          <Ionicons name="close" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  fullScreen: {
    flex: 1,
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    backgroundColor: '#000',
  },
  regularWrapper: {
    flex: 1,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  webVideo: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  closeBtn: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 60 : 30,
    right: 20,
    zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loader: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  controlsOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  playPauseBtn: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  bottomControls: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    paddingHorizontal: 40,
  },
  timeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  timeText: {
    color: '#FFF',
    fontSize: 12,
    fontWeight: '700',
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  progressBarWrapper: {
    height: 20,
    justifyContent: 'center',
  },
  progressBarBackground: {
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#7c3aed',
  },
});

export default VideoModal;
